import type { AppConfig } from '@/config/AppConfig';
import { getRuntimeConfig } from '@/config/runtimeConfig';
import { ENDPOINTS } from '@/generated/endpoints';
import { checkIsAxiosTransientError } from '@/shared/utils/error-handler';
import { ValidationError } from '@/shared/utils/errors/error-classes';
import { isChunkLoadError } from './helpers/chunk-load-error';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const STATUS_CODES_TO_IGNORE = [
  401, // authentication — session expiry handled by Auth0, not a code bug
  403, // access denied — expected authorization failures, handled gracefully in UI
  404, // not found — expected not found errors, handled gracefully in UI
  429, // rate limit — expected throttling, retried by the app
];

/**
 * Per-endpoint fingerprinting for failed HTTP requests.
 *
 * `httpClientIntegration` reports every failed request with the same message
 * ("HTTP Client Error with status code: N"), so Sentry's default grouping
 * collapses every backend endpoint into a single catch-all issue. We override
 * the fingerprint with the generated operation name + status code so each
 * endpoint+status gets its own issue that can be alerted on and routed
 * independently.
 *
 * Matching the request path against the generated endpoint registry (rather
 * than guessing which path segments are opaque IDs) keeps this precise and
 * self-maintaining: new endpoints are covered automatically on the next
 * `yarn generate:client`.
 *
 * Each `{param}` placeholder becomes a single path segment that excludes `:`,
 * so REST detail routes (`/automations/{automation}`) stay distinct from
 * custom verbs on the same collection (`/automations/{automation}:activate`).
 *
 * Matchers are ordered most-specific-first (fewest params) so that when a
 * concrete URL matches both a literal route and a `{param}` route (e.g.
 * `/credentials/kinds` vs `/credentials/{credential_id}`), the literal one
 * wins. Ties between equal-param routes fall back to registry order.
 */
const ENDPOINT_MATCHERS: ReadonlyArray<{
  name: string;
  method: string;
  pattern: RegExp;
  paramCount: number;
}> = Object.entries(ENDPOINTS)
  .map(([name, { method, path }]) => {
    const PARAM_SENTINEL = '\x00';
    const source = path
      .replace(/\{[^}]+\}/g, PARAM_SENTINEL) // path params -> sentinel
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // escape regex metacharacters
      .split(PARAM_SENTINEL)
      .join('[^/:]+'); // sentinel -> a single path segment (excludes custom verbs)
    const paramCount = (path.match(/\{[^}]+\}/g) ?? []).length;
    return { name, method, pattern: new RegExp(`^${source}$`), paramCount };
  })
  .sort((a, b) => a.paramCount - b.paramCount);

/**
 * Resolve a request path + method to its generated operation name, or
 * `undefined` if the method is unknown or the path is not a known backend
 * endpoint (third-party URLs, CDN assets, etc.). Requiring a method match
 * avoids cross-attributing a GET-only and POST-only endpoint that share a path.
 */
function resolveOperationName(
  pathname: string,
  method: string | undefined,
): string | undefined {
  if (!method) return undefined;
  const upperMethod = method.toUpperCase();
  return ENDPOINT_MATCHERS.find(
    matcher => matcher.method === upperMethod && matcher.pattern.test(pathname),
  )?.name;
}

/**
 * Calculate the replay session sample rate based on environment.
 *
 * @param {string | undefined} environment - The environment name (prod, staging, dev, local, etc.)
 * @param {boolean} enableTracing - Whether tracing is enabled
 * @returns {number} Replay sample rate (0.0 to 1.0)
 */
function getReplaySessionSampleRate(
  environment: string | undefined,
  enableTracing: boolean,
): number {
  // Replays disabled if tracing is disabled
  if (!enableTracing) {
    return 0;
  }

  // Production: Capture 100% of sessions for maximum visibility
  if (environment === 'prod') {
    return 1.0;
  }

  // Other environments: Capture 50% of sessions
  return 0.5;
}

/**
 * Initialize Sentry with project-specific configuration
 */
export function initSentry(config?: AppConfig) {
  // Don't initialize Sentry during local development
  if (import.meta.env.DEV) {
    return;
  }

  // Don't initialize Sentry when running on localhost (any port)
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return;
  }

  // Get config from passed config or runtime config (always present in production)
  const runtimeConfig = config ? null : getRuntimeConfig();
  const sentryDsn = config?.sentryDsn ?? runtimeConfig?.sentryDsn;
  const environment = runtimeConfig?.environment;

  // Enable tracing for all environments except 'local' or when env is missing
  // - 'local' is set by yarn dev for local development
  // - Missing env means we can't determine the environment, so skip tracing
  // - Sentry charges based on traces, so we only enable for known deployed environments
  const enableTracing = Boolean(environment) && environment !== 'local';

  Sentry.init({
    enableLogs: true,
    attachStacktrace: true,

    // Unique URL that connects to your Sentry project
    // Use DSN from auth0 config (fetched from AWS SSM during build)
    dsn: sentryDsn,
    // Environment context from runtime config
    environment,
    // Commit SHA injected at build time — ties errors to the release that produced them
    release: __SENTRY_RELEASE__ || undefined,
    // Ignore errors originating from browser extensions — not our code
    denyUrls: [
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    integrations: [
      // Tracks React Router navigation and performance
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ['log', 'info', 'warn', 'error'],
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [window.location.origin],
        networkRequestHeaders: ['x-kog-request-id', 'x-amz-cf-id'],
      }),
      // Tracks outgoing HTTP requests with W3C Trace Context
      Sentry.browserTracingIntegration({
        // Automatically instrument fetch and XHR
        traceFetch: true,
        traceXHR: true,
      }),
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration({
        failedRequestTargets: [window.location.origin],
      }),
    ],
    // Include user info (IP, etc) in error reports
    sendDefaultPii: true,
    // Percentage of performance traces to capture (1.0 = 100%)
    // Tracing is enabled for all environments except 'local' (Sentry charges based on traces)
    tracesSampleRate: enableTracing ? 0.1 : 0,

    // Percentage of session replays to capture (1.0 = 100%)
    // Production: 100%, Other environments: 50%, Local: 0%
    replaysSessionSampleRate: getReplaySessionSampleRate(
      environment,
      enableTracing,
    ),
    // Percentage of replays to capture on error (1.0 = 100%)
    replaysOnErrorSampleRate: enableTracing ? 1.0 : 0,

    // Drop expected, non-actionable errors before they reach Sentry
    beforeSend(event, hint) {
      const error = hint.originalException;
      // Resolve the HTTP status regardless of which field/path exposes it.
      // Different error paths expose it differently and MUST all be covered, or
      // the drop below silently misses them:
      //   - httpClientIntegration:        `event.contexts.response.status_code`
      //   - Axios / stream errors:        `.status`
      //   - AxiosError with a response:   `.response.status`
      //   - `ApiError`:                   `.statusCode`
      //   - `RainUploadError` and friends: `.httpStatus`
      const errorWithStatus = error as
        | (Error & {
            status?: number;
            statusCode?: number;
            httpStatus?: number;
            response?: { status?: number };
          })
        | undefined;
      const responseStatusCode = (
        event.contexts?.response as { status_code?: number } | undefined
      )?.status_code;
      const httpStatus =
        responseStatusCode ??
        (error instanceof Error
          ? (errorWithStatus?.status ??
            errorWithStatus?.response?.status ??
            errorWithStatus?.statusCode ??
            errorWithStatus?.httpStatus)
          : undefined);
      // Drop expected HTTP responses (401/403/404/429) outright — these are
      // handled gracefully in the UI and are not actionable.
      if (httpStatus && STATUS_CODES_TO_IGNORE.includes(httpStatus)) {
        return null;
      }

      // Keep 5xx responses, but tag them so backend failures can be filtered
      // out from genuine frontend defects. Server errors are backend bugs, not
      // frontend defects, but capturing them (labeled) lets us correlate
      // backend incidents with the frontend impact instead of losing them.
      const isServerError = Boolean(httpStatus && httpStatus >= 500);
      if (isServerError) {
        event.tags = {
          ...event.tags,
          error_source: 'backend',
        };
      }

      // Transient network errors (timeouts, offline, DNS) — retried elsewhere, not bugs
      if (checkIsAxiosTransientError(error)) {
        return null;
      }

      // Truncated JSON from SSE streams — not a code bug, just network noise
      if (
        error instanceof SyntaxError &&
        error.message === 'Unexpected end of JSON input'
      ) {
        return null;
      }

      // Stale chunk URLs after a deploy — the previous build's hashed assets
      // stop existing when the new pod serves traffic, so a session still
      // running the old module graph 404s on a route chunk. Recovered by a
      // reload (see the `vite:preloadError` listener in `src/main.tsx` and the
      // reload prompt in `RouteErrorBoundary`), so it is deploy churn, not a
      // defect. This must be checked BEFORE the network filter below: Chromium's
      // wording happens to contain "Failed to fetch" and so was dropped there by
      // accident, while WebKit's wording matched nothing and was reported as a
      // frontend defect for months (BUMBLEBEE-B4).
      const isChunkError =
        isChunkLoadError(error) ||
        // Reports with no error object carry the wording on the event instead.
        (!(error instanceof Error) &&
          isChunkLoadError(event.exception?.values?.[0]?.value));
      if (isChunkError) {
        return null;
      }

      // Native fetch network failures — user environment issues, not code bugs
      if (
        error instanceof TypeError &&
        /Failed to fetch|network error|Load failed|NetworkError when attempting to fetch resource/.test(
          error.message,
        )
      ) {
        return null;
      }

      // Abort errors — user navigated away or component unmounted
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null;
      }

      // React commit-phase DOM races (removeChild/insertBefore "not a child")
      // — not app logic bugs. These fire when a browser auto-translate
      // extension (Google/Edge Translate) mutates text nodes React owns, so
      // React's later removal targets a node the extension already swapped.
      // Already swallowed by an ErrorBoundary; nothing breaks for the user.
      if (
        error instanceof DOMException &&
        error.name === 'NotFoundError' &&
        /removeChild|insertBefore/.test(error.message)
      ) {
        return null;
      }

      // Validation errors — expected user input, handled in UI
      if (error instanceof ValidationError) {
        return null;
      }

      // ResizeObserver loop — benign browser behavior, not actionable
      if (
        error instanceof Error &&
        error.message.includes('ResizeObserver loop')
      ) {
        return null;
      }

      // Split the catch-all "HTTP Client Error" / "AxiosError" group into one
      // issue per endpoint + status, so each endpoint can be alerted on and
      // triaged independently. See ENDPOINT_MATCHERS above.
      //
      // By the time we get here, 401/403/404/429 have been dropped above. Both
      // actionable client errors (400/409/422/…) and 5xx server errors flow
      // through: 4xx means the frontend constructed the request wrongly (a
      // frontend defect worth its own issue), while 5xx is tagged
      // `error_source: backend` above so backend failures can be filtered out.
      //
      // Two distinct paths reach Sentry for a failed request:
      //   1. httpClientIntegration auto-captures it and populates
      //      `contexts.response.status_code` + `event.request.url`.
      //   2. App code captures the rejected AxiosError via `captureException`
      //      (handled: yes). Here the response context is NOT populated, so we
      //      fall back to the AxiosError's own `config`/`response` fields.
      //      Without this fallback every handled AxiosError collapses into a
      //      single "AxiosError: Request failed with status code N" group.
      //
      // Gating on frame.in_app to skip events with "real" app
      // frames in the stack does not work. In a bundled build, vendor code
      // (Axios, TanStack Query, etc.) is served from /assets/*.js so the
      // SDK marks every frame in_app: true client-side.
      // Source-map resolution to node_modules paths only happens server-side
      // after beforeSend has already run, making any in_app check here
      // permanently wrong for deployed builds.
      const axiosError = isAxiosError(error) ? error : undefined;

      // The two capture paths expose the request on different objects, and they
      // must NOT be mixed: for httpClientIntegration `event.request.url` is the
      // failed API URL, but for a handled AxiosError `event.request.url` is the
      // current page route (the browser location) while the API path lives on
      // `axiosError.config.url`. Read url + method + status from one consistent
      // source.
      let status: number | undefined;
      let requestUrl: string | undefined;
      let requestMethod: string | undefined;

      if (responseStatusCode) {
        status = responseStatusCode;
        requestUrl = event.request?.url;
        requestMethod = event.request?.method;
      } else if (axiosError) {
        status = axiosError.response?.status ?? axiosError.status;
        requestUrl = axiosError.config?.url;
        requestMethod = axiosError.config?.method;
      }

      // Prefix backend 5xx distinctly so they never collapse into the same
      // issue as a frontend client error on the same endpoint. Derive it from
      // the authoritative `httpStatus` (not the block-local `status`, which is
      // only populated for the httpClientIntegration/AxiosError paths) so the
      // prefix stays consistent with the `error_source` tag above.
      const fingerprintPrefix = isServerError
        ? 'http-backend-error'
        : 'http-client-error';

      if (status && requestUrl) {
        let pathname: string;
        try {
          pathname = new URL(requestUrl).pathname;
        } catch {
          pathname = requestUrl.split('?')[0];
        }

        const operation = resolveOperationName(pathname, requestMethod);
        if (operation) {
          event.fingerprint = [fingerprintPrefix, String(status), operation];
          event.tags = {
            ...event.tags,
            endpoint: operation,
            status_code: String(status),
          };

          // Make each split issue self-describing in the Sentry issues list.
          const exception = event.exception?.values?.[0];
          if (exception) {
            exception.value = `HTTP ${status} · ${operation}`;
          }
        }
      } else if (isServerError && httpStatus) {
        // Backend 5xx that can't be resolved to an endpoint (e.g. a plain
        // stream Error carrying only `.status`, no request URL). Still group it
        // under the backend bucket by status so it stays separate from
        // frontend issues instead of falling back to default message grouping.
        event.fingerprint = [fingerprintPrefix, String(httpStatus)];
      }

      return event;
    },

    // URLs to which Sentry will attach tracing headers (traceparent, baggage)
    // This ensures distributed tracing works across frontend and backend
    // Match all API calls to our backend
    tracePropagationTargets: [
      /^\/api\//,
      // Add your backend domain if different from frontend
      // e.g., 'api.example.com', /^https:\/\/api\.example\.com/
    ],
  });
}

/**
 * Sentry error handler for uncaught errors
 * Catches errors that Error Boundaries miss:
 * - Event handlers (onClick, onChange, etc.)
 * - Async code (promises, setTimeout)
 * - Side effects (useEffect callbacks)
 * - Any other uncaught JavaScript errors
 */
export const sentryErrorHandler = Sentry.reactErrorHandler(
  (error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  },
);
